"use client";

import {createMenu} from "@gluestack-ui/menu";
import type {VariantProps} from "@gluestack-ui/nativewind-utils";
import {tva} from "@gluestack-ui/nativewind-utils/tva";
import {AnimatePresence, Motion} from "@legendapp/motion";
import {cssInterop} from "nativewind";
import type {ComponentType} from "react";
import React from "react";
import {View} from "react-native";

import type {IPressableProps} from "../pressable";
import {Pressable} from "../pressable";
import {Text} from "../text";

const menuStyle = tva({
    base:
        "rounded-md " +
        "bg-background-0 " +
        "border " +
        "border-outline-100 " +
        "p-0 " +
        "shadow-hard-5 " +
        "max-h-[500px] " +
        "overflow-y-auto "
});

const menuItemStyle = tva({
    base:
        "p-2 " +
        "border-b " +
        "web:last:border-b-transparent " +
        "border-blueGray-100 " +
        "flex-row " +
        "items-center " +
        "rounded-sm " +
        "data-[hover=true]:bg-background-50 " +
        "data-[active=true]:bg-background-100 " +
        "data-[focus=true]:bg-background-50 " +
        "data-[focus=true]:web:outline-none " +
        "data-[focus=true]:web:outline-0 " +
        "data-[disabled=true]:opacity-40 " +
        "data-[disabled=true]:web:cursor-not-allowed " +
        "data-[focus-visible=true]:web:outline-2 " +
        "data-[focus-visible=true]:web:outline-primary-700 " +
        "data-[focus-visible=true]:web:outline " +
        "data-[focus-visible=true]:web:cursor-pointer " +
        "data-[disabled=true]:data-[focus=true]:bg-transparent"
});

const menuBackdropStyle = tva({
    base: "absolute top-0 bottom-0 left-0 right-0 web:cursor-default"
    // add this classnames if you want to give background color to backdrop
    // opacity-50 bg-background-500,
});

const menuSeparatorStyle = tva({
    base: "bg-background-200 h-px w-full"
});

const menuItemLabelStyle = tva({
    base: "font-body",

    variants: {
        isTruncated: {
            true: "web:truncate"
        },
        bold: {
            true: "font-bold"
        },
        underline: {
            true: "underline"
        },
        strikeThrough: {
            true: "line-through"
        },
        size: {
            "3xs": "text-3xs font-medium leading-sm",
            "2xs": "text-2xs font-medium leading-sm",
            xs: "text-xs font-medium leading-sm",
            sm: "text-sm font-medium leading-sm",
            md: "text-md font-medium leading-sm",
            lg: "text-lg font-medium leading-sm",
            xl: "text-xl font-medium leading-sm",
            "2xl": "text-2xl font-medium leading-sm",
            "3xl": "text-3xl font-medium leading-sm",
            "4xl": "text-4xl font-medium leading-sm"
        },
        color: {
            "success.600": "text-success-600",
            "warning.600": "text-warning-600",
            "danger.600": "text-danger-600",
            "red.600": "text-red-600",
            "error.600": "text-error-600",
            "blueGray.400": "text-blueGray-400",
            "blueGray.600": "text-blueGray-600",
            "blueGray.800": "text-blueGray-800",
            "blue.300": "text-blue-300",
            "blue.600": "text-blue-600",
            "blue.900": "text-blue-900",
            "primary.600": "text-blue-600",
            white: "text-white",
            HomeAway: "text-HomeAway-text",
            Airbnb: "text-Airbnb-text",
            Booking: "text-Booking-text",
            Houfy: "text-Houfy-text",
            internal: "text-internal-text"
        },
        sub: {
            true: "text-xs"
        },
        italic: {
            true: "italic"
        },
        highlight: {
            true: "bg-yellow-500"
        }
    }
});

const BackdropPressable = React.forwardRef<
    React.ComponentRef<typeof Pressable<never>>,
    React.ComponentPropsWithoutRef<typeof Pressable> & VariantProps<typeof menuBackdropStyle>
>(function BackdropPressable({className, ...props}, ref) {
    return (
        <Pressable
            ref={ref}
            className={menuBackdropStyle({
                class: className
            })}
            {...props}
        />
    );
});

type IMenuItemProps = VariantProps<typeof menuItemStyle> & {
    className?: string;
} & IPressableProps;

const Item = React.forwardRef<React.ComponentRef<typeof Pressable<never>>, IMenuItemProps>(
    function MenuItem({className, ...props}, ref) {
        return (
            <Pressable
                ref={ref}
                className={menuItemStyle({
                    class: className
                })}
                role="menuitem"
                {...props}
            />
        );
    }
);

const Separator = React.forwardRef(function MenuSeparator({className, ...props}: any, ref?: any) {
    return <View ref={ref} className={menuSeparatorStyle({class: className})} {...props} />;
});
export const UIMenu = createMenu({
    // @ts-expect-error
    Root: Motion.View,
    Item,
    Label: Text,
    Backdrop: BackdropPressable,
    AnimatePresence,
    Separator
});

cssInterop(
    Motion.View as ComponentType<{
        style: unknown;
    }>,
    {className: "style"}
);

export type IMenuProps = React.ComponentProps<typeof UIMenu> &
    VariantProps<typeof menuStyle> & {className?: string};
type IMenuItemLabelProps = React.ComponentProps<typeof UIMenu.ItemLabel> &
    VariantProps<typeof menuItemLabelStyle> & {className?: string};

const Menu = React.forwardRef<React.ElementRef<typeof UIMenu>, IMenuProps>(
    ({className, ...props}, ref) => {
        return (
            <UIMenu
                ref={ref}
                animate={{
                    opacity: 1,
                    scale: 1
                }}
                className={menuStyle({
                    class: className
                })}
                exit={{
                    opacity: 0,
                    scale: 0.8
                }}
                initial={{
                    opacity: 0,
                    scale: 0.8
                }}
                transition={{
                    type: "timing",
                    duration: 100
                }}
                {...props}
            />
        );
    }
);

const MenuItem = UIMenu.Item;

const MenuItemLabel = React.forwardRef<
    React.ComponentRef<typeof UIMenu.ItemLabel>,
    IMenuItemLabelProps
>(
    (
        {
            className,
            isTruncated,
            bold,
            underline,
            strikeThrough,
            size = "sm",
            sub,
            italic,
            highlight,
            color = "blueGray.600",
            ...props
        },
        ref
    ) => {
        return (
            <UIMenu.ItemLabel
                ref={ref}
                className={menuItemLabelStyle({
                    isTruncated,
                    bold,
                    underline,
                    strikeThrough,
                    size,
                    sub,
                    italic,
                    highlight,
                    color,
                    class: className
                })}
                {...props}
            />
        );
    }
);

const MenuSeparator = UIMenu.Separator;

Menu.displayName = "Menu";
MenuItem.displayName = "MenuItem";
MenuItemLabel.displayName = "MenuItemLabel";
MenuSeparator.displayName = "MenuSeparator";
export {Menu, MenuItem, MenuItemLabel, MenuSeparator};
